<template>
    <div class="product-search-wrap">
        <form class="product-search-form">
            <!-- Base filters -->
            <div class="inline-search-fields">
                <div class="inline-field">
                    <div class="field-name">{{ $t('Product name (or sku Id)') }}</div>
                    <div class="field-value">
                        <input type="text" v-model="searchData.name">
                    </div>
                </div>
                <div class="inline-field">
                    <div class="field-name">{{ $t('Price') }}</div>
                    <div class="field-value field-value-inline">
                        <input type="number" v-model.number="searchData.price.from">
                        <span class="slash">-</span>
                        <input type="number" v-model.number="searchData.price.to">
                        {{ `&nbsp;` + currencySign }}
                    </div>
                </div>
                <div class="inline-field">
                    <div class="field-name">{{ $t('Search in category') }}</div>
                    <div class="field-value">
                        <div v-if="isDataLoading" class="loader"></div>
                        <div v-else>
                            <multiselect v-model="searchData.category"
                                         :placeholder="$t('Select category')"
                                         label="name"
                                         track-by="id"
                                         :options="categoriesList"
                                         :showLabels="false"
                                         :multiple="false"
                            ></multiselect>
                        </div>
                    </div>
                </div>
                <div class="inline-field">
                    <div class="field-name">{{ $t('Skus') }}</div>
                    <div class="field-value">
                        <base-checkbox :label="$t('Show skus in results')" v-model="searchData.showSkus" :checked="searchData.showSkus"></base-checkbox>
                    </div>
                </div>
                <div class="inline-field">
                    <div class="field-name">{{ $t('Compare price') }}</div>
                    <div class="field-value">
                        <base-checkbox :label="$t('Show products with compare price')" v-model="searchData.comparePrice" :checked="searchData.comparePrice"></base-checkbox>
                    </div>
                </div>
                <div class="inline-field">
                    <div class="field-name">{{ $t('Rating') }}</div>
                    <div class="field-value rating-block">
                        <select v-model="searchData.rating.sign" class="inherit">
                            <option value=""></option>
                            <option value="eq_num">=</option>
                            <option value="gt">&gt;</option>
                            <option value="gte">&gt;=</option>
                            <option value="lt">&lt;</option>
                            <option value="lte">&lt;=</option>
                        </select>
                        <base-rating :stars="5" v-model="searchData.rating.value"></base-rating>
                    </div>
                </div>
                <!-- Order by -->
                <div class="block-field">
                    <div class="field-value">
                        <a href="#" class="inline-link" @click.prevent="showOrderBy = !showOrderBy">
                            <b><i>
                                {{ $t('Sort results by') }}
                            </i></b>
                            <i class="gc-icon" :class="showOrderBy ? 'darr' : 'rarr'"></i>
                        </a>
                        <a href="#" @click.prevent="searchData.order = { type: '', direction: '' }" v-show="searchData.order.type !== ''" class="reset red">{{ $t('cancel') }}</a>
                    </div>
                </div>
                <div class="block-field" v-show="showOrderBy">
                    <div class="inline-search-fields">
                        <div class="inline-field">
                            <div class="field-name">{{ $t('Name') }}</div>
                            <div class="field-value">
                                <label class="mb right"><input type="radio" v-model="searchData.order" :value="{ type: 'name', direction: 'asc' }"> {{ $t('ascending') }}</label>
                                <label class="mb left"><input type="radio" v-model="searchData.order" :value="{ type: 'name', direction: 'desc' }"> {{ $t('descending') }}</label>
                            </div>
                        </div>
                        <div class="inline-field">
                            <div class="field-name">{{ $t('Price') }}</div>
                            <div class="field-value">
                                <label class="mb right"><input type="radio" v-model="searchData.order" :value="{ type: 'price', direction: 'asc' }"> {{ $t('ascending') }}</label>
                                <label class="mb left"><input type="radio" v-model="searchData.order" :value="{ type: 'price', direction: 'desc' }"> {{ $t('descending') }}</label>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Additional filters -->
                <div class="block-field">
                    <div class="field-value">
                        <a href="#" class="inline-link" @click.prevent="showFeatures = !showFeatures"><b><i>
                            {{ $t('Additional search parameters') }}
                        </i></b>
                            <i class="gc-icon" :class="showFeatures ? 'darr' : 'rarr'"></i>
                        </a>
                    </div>
                </div>
                <div class="block-field" v-show="showFeatures">
                    <div v-if="isDataLoading" class="loader"></div>
                    <div v-else class="inline-search-fields">
                        <div class="inline-field" v-for="(feature, key) in features"
                             :key="key"
                        >
                            <div class="field-name">{{ feature.name }}</div>
                            <div class="field-value">
                                <multiselect v-model="searchData.features[key + `***` + feature.id]"
                                             :placeholder="$t('Select value')"
                                             label="name"
                                             track-by="id"
                                             :options="feature.values"
                                             :showLabels="false"
                                             :multiple="true"
                                ></multiselect>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <base-submit-block :button="$t('Search')"
                               :data.sync="searchData"
                               :saveUrl=settings.urls.search_products
                               :options="{
                                   buttonClass: 'blue',
                                   statesText: {
                                       saved: $t('Done'),
                                       disabled: $t('Wait, please. Data is loading...')
                                   },
                                   autoSubmit: autoSubmit
                               }"
                               :disabled="isDataLoading || isSelectedLoading"
                               @saved="initSearchResults"
                               @pre-save="products=[]; !autoSubmit && (searchData.page = 1)"
            ></base-submit-block>
        </form>
        <results v-if="!isSelectedLoading"
                 :products="products"
                 :type="searchType"
                 :pagination="pagination"
                 :settings="settings"
                 @page-changed="onPageChanged($event)"
        ></results>
        <div v-else class="big-loader"></div>
    </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import qs from "qs";
import ProductSearchModule from "@/components/products_search/store";

export default {
    name: "ProductsSearch",
    props: {
        currency: String,
        url: String,
        selected: {
            type: Object,
            default() {
                return {
                    url: '',
                    data: {
                        skus: [],
                        products: []
                    }
                };
            }
        },
        settings: Object
    },
    components: {
        Multiselect,
        'base-submit-block': () => import('@/components/base/BaseSubmitBlock'),
        'base-checkbox': () => import('@/components/base/BaseCheckbox'),
        'base-rating': () => import('@/components/base/BaseRating'),
        'results': () => import('@/components/products_search/TheResults'),
    },
    data() {
        return {
            searchData: {
                name: '',
                price: { from: '', to: '' },
                showSkus: 0,
                comparePrice: 0,
                rating: { sign: '', value: 2 },
                features: {},
                order: { type: '', direction: '' },
                page: 1
            },
            showFeatures: false,
            showOrderBy: false,
            currencySign: '$',
            categories: [],
            features: [],
            isDataLoading: true,
            isSelectedLoading: false,
            products: [],
            pagination: null,
            autoSubmit: false,
            searchType: 'products'
        }
    },
    created() {
        if (!this.$store.hasModule('ProductSearchModule')) {
            this.$store.registerModule('ProductSearchModule', ProductSearchModule);
        }
        this.currencySign = this.settings.currency_info.pattern_text.replace(/(%s|\s)/g, '') || this.currency;
        this.loadData();
        this.loadSelected();
    },
    computed: {
        categoriesList() {
            let list = [{
                id: '',
                name: '* * * ' + this.$t('All') + ' * * *'
            }];
            for (let i in this.categories) {
                list.push({
                    id: this.categories[i].id,
                    name: (this.categories[i].depth ? " ".repeat(this.categories[i].depth * 4) : "") + this.categories[i].name,
                });
            }
            return list;
        }
    },
    methods: {
        /* Подгружаем категории и характеристики */
        loadData() {
            const self = this;
            if (self.url) {
                self.$http.post(self.url,
                    qs.stringify({
                        _csrf: this.csrf,
                        category_type: 1
                    }), { headers: { 'content-type': 'application/x-www-form-urlencoded' } }
                ).then(function (response) {
                    self.categories = response?.data?.data?.categories || [];
                    self.features = response?.data?.data?.features || [];
                }).finally(function () {
                    self.isDataLoading = false;
                });
            }
        },
        loadSelected() {
            const self = this;
            if (self.selected.url && (self.selected.data.skus.length || self.selected.data.products.length)) {
                self.autoSubmit = false;
                self.isSelectedLoading = true;
                self.$http.post(self.selected.url,
                    qs.stringify({
                        _csrf: self.csrf,
                        data: self.selected.data
                    }), { headers: { 'content-type': 'application/x-www-form-urlencoded' } }
                ).then(function (response) {
                    self.products = response?.data?.data?.products;
                    self.pagination = response?.data?.data?.pagination;
                }).finally(function () {
                    self.isSelectedLoading = false;
                });
            } else {
                self.$store.commit('ProductSearchModule/update', []);
            }
        },
        onPageChanged(page) {
            const isAutoSubmit = this.searchData.page !== page;
            this.searchData.page = page;
            if (isAutoSubmit) {
                this.autoSubmit = false;
                this.autoSubmit = true;
            }
        },
        /* Передаем подгруженные данные в блок с отображением результатов */
        initSearchResults(response) {
            this.autoSubmit = false;
            this.products = response?.data?.data?.products;
            this.pagination = response?.data?.data?.pagination;
            this.searchType = this.searchData.showSkus ? 'skus' : 'products';
        }
    }
}
</script>
<style scoped>
.product-search-form { overflow: visible; }
.inline-search-fields { display: flex; flex-flow: row wrap; justify-content: flex-start; }
.inline-field { display: flex; flex-flow: row wrap; width: calc(100% / 3 - 10px); flex-direction: column; margin-left: 15px; }
.inline-field:nth-child(3n+1) { margin-left: 0; }
.inline-field .field-value-inline { display: flex; flex-flow: row nowrap; justify-content: space-around; align-items: center; }
.field-value .slash { padding: 0 5px; }
.field-name { color: #aaa; font-size: .9em; margin-bottom: 5px; }
.field-value input[type='text'], .field-value input[type='number'], .field-value select { padding: 5px; width: 100%; box-sizing: border-box; }
.field-value select.inherit { width: inherit; }
.block-field { width: 100%; }
.block-field, .inline-field { margin-bottom: 15px; }
.loader { width: 16px; height: 16px; background: url("data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+PHN2ZyB4bWxuczpzdmc9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB2ZXJzaW9uPSIxLjAiIHdpZHRoPSI2NHB4IiBoZWlnaHQ9IjY0cHgiIHZpZXdCb3g9IjAgMCAxMjggMTI4IiB4bWw6c3BhY2U9InByZXNlcnZlIj48Zz48Y2lyY2xlIGN4PSIxNiIgY3k9IjY0IiByPSIxNiIgZmlsbD0iIzAwMDAwMCIgZmlsbC1vcGFjaXR5PSIxIi8+PGNpcmNsZSBjeD0iMTYiIGN5PSI2NCIgcj0iMTQuMzQ0IiBmaWxsPSIjMDAwMDAwIiBmaWxsLW9wYWNpdHk9IjEiIHRyYW5zZm9ybT0icm90YXRlKDQ1IDY0IDY0KSIvPjxjaXJjbGUgY3g9IjE2IiBjeT0iNjQiIHI9IjEyLjUzMSIgZmlsbD0iIzAwMDAwMCIgZmlsbC1vcGFjaXR5PSIxIiB0cmFuc2Zvcm09InJvdGF0ZSg5MCA2NCA2NCkiLz48Y2lyY2xlIGN4PSIxNiIgY3k9IjY0IiByPSIxMC43NSIgZmlsbD0iIzAwMDAwMCIgZmlsbC1vcGFjaXR5PSIxIiB0cmFuc2Zvcm09InJvdGF0ZSgxMzUgNjQgNjQpIi8+PGNpcmNsZSBjeD0iMTYiIGN5PSI2NCIgcj0iMTAuMDYzIiBmaWxsPSIjMDAwMDAwIiBmaWxsLW9wYWNpdHk9IjEiIHRyYW5zZm9ybT0icm90YXRlKDE4MCA2NCA2NCkiLz48Y2lyY2xlIGN4PSIxNiIgY3k9IjY0IiByPSI4LjA2MyIgZmlsbD0iIzAwMDAwMCIgZmlsbC1vcGFjaXR5PSIxIiB0cmFuc2Zvcm09InJvdGF0ZSgyMjUgNjQgNjQpIi8+PGNpcmNsZSBjeD0iMTYiIGN5PSI2NCIgcj0iNi40MzgiIGZpbGw9IiMwMDAwMDAiIGZpbGwtb3BhY2l0eT0iMSIgdHJhbnNmb3JtPSJyb3RhdGUoMjcwIDY0IDY0KSIvPjxjaXJjbGUgY3g9IjE2IiBjeT0iNjQiIHI9IjUuMzc1IiBmaWxsPSIjMDAwMDAwIiBmaWxsLW9wYWNpdHk9IjEiIHRyYW5zZm9ybT0icm90YXRlKDMxNSA2NCA2NCkiLz48YW5pbWF0ZVRyYW5zZm9ybSBhdHRyaWJ1dGVOYW1lPSJ0cmFuc2Zvcm0iIHR5cGU9InJvdGF0ZSIgdmFsdWVzPSIwIDY0IDY0OzMxNSA2NCA2NDsyNzAgNjQgNjQ7MjI1IDY0IDY0OzE4MCA2NCA2NDsxMzUgNjQgNjQ7OTAgNjQgNjQ7NDUgNjQgNjQiIGNhbGNNb2RlPSJkaXNjcmV0ZSIgZHVyPSI3MjBtcyIgcmVwZWF0Q291bnQ9ImluZGVmaW5pdGUiPjwvYW5pbWF0ZVRyYW5zZm9ybT48L2c+PC9zdmc+") no-repeat; background-size: 16px; }
.big-loader { width: 54px; height: 54px; margin: 0 auto; background: url("data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+PHN2ZyB4bWxuczpzdmc9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB2ZXJzaW9uPSIxLjAiIHdpZHRoPSI2NHB4IiBoZWlnaHQ9IjY0cHgiIHZpZXdCb3g9IjAgMCAxMjggMTI4IiB4bWw6c3BhY2U9InByZXNlcnZlIj48Zz48Y2lyY2xlIGN4PSIxNiIgY3k9IjY0IiByPSIxNiIgZmlsbD0iIzAwMDAwMCIgZmlsbC1vcGFjaXR5PSIxIi8+PGNpcmNsZSBjeD0iMTYiIGN5PSI2NCIgcj0iMTQuMzQ0IiBmaWxsPSIjMDAwMDAwIiBmaWxsLW9wYWNpdHk9IjEiIHRyYW5zZm9ybT0icm90YXRlKDQ1IDY0IDY0KSIvPjxjaXJjbGUgY3g9IjE2IiBjeT0iNjQiIHI9IjEyLjUzMSIgZmlsbD0iIzAwMDAwMCIgZmlsbC1vcGFjaXR5PSIxIiB0cmFuc2Zvcm09InJvdGF0ZSg5MCA2NCA2NCkiLz48Y2lyY2xlIGN4PSIxNiIgY3k9IjY0IiByPSIxMC43NSIgZmlsbD0iIzAwMDAwMCIgZmlsbC1vcGFjaXR5PSIxIiB0cmFuc2Zvcm09InJvdGF0ZSgxMzUgNjQgNjQpIi8+PGNpcmNsZSBjeD0iMTYiIGN5PSI2NCIgcj0iMTAuMDYzIiBmaWxsPSIjMDAwMDAwIiBmaWxsLW9wYWNpdHk9IjEiIHRyYW5zZm9ybT0icm90YXRlKDE4MCA2NCA2NCkiLz48Y2lyY2xlIGN4PSIxNiIgY3k9IjY0IiByPSI4LjA2MyIgZmlsbD0iIzAwMDAwMCIgZmlsbC1vcGFjaXR5PSIxIiB0cmFuc2Zvcm09InJvdGF0ZSgyMjUgNjQgNjQpIi8+PGNpcmNsZSBjeD0iMTYiIGN5PSI2NCIgcj0iNi40MzgiIGZpbGw9IiMwMDAwMDAiIGZpbGwtb3BhY2l0eT0iMSIgdHJhbnNmb3JtPSJyb3RhdGUoMjcwIDY0IDY0KSIvPjxjaXJjbGUgY3g9IjE2IiBjeT0iNjQiIHI9IjUuMzc1IiBmaWxsPSIjMDAwMDAwIiBmaWxsLW9wYWNpdHk9IjEiIHRyYW5zZm9ybT0icm90YXRlKDMxNSA2NCA2NCkiLz48YW5pbWF0ZVRyYW5zZm9ybSBhdHRyaWJ1dGVOYW1lPSJ0cmFuc2Zvcm0iIHR5cGU9InJvdGF0ZSIgdmFsdWVzPSIwIDY0IDY0OzMxNSA2NCA2NDsyNzAgNjQgNjQ7MjI1IDY0IDY0OzE4MCA2NCA2NDsxMzUgNjQgNjQ7OTAgNjQgNjQ7NDUgNjQgNjQiIGNhbGNNb2RlPSJkaXNjcmV0ZSIgZHVyPSI3MjBtcyIgcmVwZWF0Q291bnQ9ImluZGVmaW5pdGUiPjwvYW5pbWF0ZVRyYW5zZm9ybT48L2c+PC9zdmc+") no-repeat; background-size: 54px; }
.rating-block { display: flex; flex-flow: row nowrap; align-items: center; }
.rating-block select { margin-right: 10px; }
.product-search-wrap >>> i.gc-icon { background-color: #666; height: 12px; width: 12px; display: inline-block; text-indent: -9999px; margin: 0 3px; text-decoration: none !important; }
.product-search-wrap >>> i.gc-icon.sort { mask-image: url("./assets/svg/sort.svg"); }
.product-search-wrap >>> i.gc-icon.new-window { mask-image: url("./assets/svg/new-window.svg"); }
.product-search-wrap >>> i.gc-icon.darr { mask-image: url("./assets/svg/darr.svg"); height: 10px; width: 10px; }
.product-search-wrap >>> i.gc-icon.rarr { mask-image: url("./assets/svg/rarr.svg"); height: 10px; width: 10px; }
.reset { font-size: .9em; }

@media (max-width: 760px) {
    .inline-field { width: calc(100% / 2 - 10px); }
    .inline-field:nth-child(3n+1) { margin-left: inherit; }
    .inline-field:nth-child(2n+1) { margin-left: 0; }
}
@media (max-width: 515px) {
    .inline-field { width: 100%; }
    .inline-field:nth-child(3n+1), .inline-field:nth-child(2n+1), .inline-field { margin-left: 0; }
}
</style>
<style>
#yourshop .product-search-wrap .icon10, #yourshop .product-search-wrap .icon16 { display: none; }
</style>